import React, { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { usePOTemplateDnd } from './contextProvider/POTemplateDndContext';
import CustomDatePicker from '../../../../Components/Utility/DatePicker/DatePicker';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import * as SystemMastersAction from '../../../SystemMasters/Store/SystemMasters.action';
import '../POTemplate.css';
import { currencySymbols } from '../../../../Components/helper';

export default function OrderDetails({ isPreviewMode = false }) {
  const { t } = useTranslation('purchase-order');
  const dispatch = useDispatch();
  const { detailState, setDetailState, setValidationError, clearValidationError } = usePOTemplateDnd();
  const lengthVisible = useSelector((state) => state.SystemMastersDetails?.lengthVisible);
  const userInformation = localStorage.getItem('loggedInUserInfo');
  const userInformationObj = useMemo(() => {
    try {
      return JSON.parse(userInformation || 'null');
    } catch (e) {
      return null;
    }
  }, [userInformation]);
  const currency = userInformationObj?.companyCurrency;
  const currencySymbol = useMemo(() => currencySymbols[currency]?.symbol, [currency]); // need to check this

  // Fetch length access on component mount
  useEffect(() => {
    dispatch(SystemMastersAction.getLengthAccess());
  }, [dispatch]);
  const columns = useMemo(() => {
    return [
      {
        key: 'packType',
        label: t('PO_TEMPLATE.DETAIL_PAGE.PACK_TYPE'),
        type: 'select',
        options: [
          { value: 'single-packed', label: t('PO_TEMPLATE.DETAIL_PAGE.SINGLE_PACKED') },
          { value: 'multiple-single-size-packed', label: t('PO_TEMPLATE.DETAIL_PAGE.MULTIPLE_SINGLE_SIZE_PACKED') },
          { value: 'multi-size-packed', label: t('PO_TEMPLATE.DETAIL_PAGE.MULTI_SIZE_PACKED') },
          { value: 'multi-destination-packed', label: t('PO_TEMPLATE.DETAIL_PAGE.MULTI_DESTINATION_PACKED') },
        ],
      },
      { key: 'productNo', label: t('PO_TEMPLATE.DETAIL_PAGE.PRODUCT_NO'), type: 'select' },
      { key: 'colourWay', label: t('PO_TEMPLATE.DETAIL_PAGE.COLOURWAY'), type: 'select' },
      { key: 'skuNumber', label: t('PO_TEMPLATE.DETAIL_PAGE.SKU_NUMBER'), type: 'text' },
      { key: 'size', label: t('PO_TEMPLATE.DETAIL_PAGE.SIZE'), type: 'select' },
      ...(lengthVisible ? [{ key: 'length', label: t('PO_TEMPLATE.DETAIL_PAGE.LENGTH'), type: 'select' }] : []),
      { key: 'ratio', label: t('PO_TEMPLATE.DETAIL_PAGE.RATIO'), type: 'number', min: 0 },
      { key: 'packs', label: t('PO_TEMPLATE.DETAIL_PAGE.PACKS'), type: 'number', min: 0 },
      { key: 'quantity', label: t('PO_TEMPLATE.DETAIL_PAGE.QUANTITY'), type: 'number', min: 0 },
      { key: 'location', label: t('PO_TEMPLATE.DETAIL_PAGE.LOCATION'), type: 'select' },
      { key: 'unitPrice', label: `${t('PO_TEMPLATE.DETAIL_PAGE.UNIT_PRICE')} (${currencySymbol})`, type: 'number', min: 0 },
      { key: 'discount', label: `${t('PO_TEMPLATE.DETAIL_PAGE.DISCOUNT')} (%)`, type: 'number', min: 0, max: 100 },
      { key: 'priceAfterDiscount', label: `${t('PO_TEMPLATE.DETAIL_PAGE.PRICE_AFTER_DISCOUNT')} (${currencySymbol})`, type: 'number', min: 0 },
      { key: 'total', label: `${t('PO_TEMPLATE.DETAIL_PAGE.TOTAL')} (${currencySymbol})`, type: 'number', min: 0 },
      { key: 'approvedByDate', label: t('PO_TEMPLATE.DETAIL_PAGE.APPROVED_BY_DATE'), type: 'date' },
      { key: 'exFactoryDate', label: t('PO_TEMPLATE.DETAIL_PAGE.EX_FACTORY_DATE'), type: 'date' },
      { key: 'shipmentDate', label: t('PO_TEMPLATE.DETAIL_PAGE.SHIPMENT_DATE'), type: 'date' },
      { key: 'action', label: '', type: 'action' },
    ];
  }, [t, lengthVisible, currencySymbol]);

  const { orderDetails } = detailState;
  const TOOLTIP_PLACEMENT = 'top';

  const actionCol = (colKey) => {
    if (colKey === 'shipmentDate' && isPreviewMode) {
      return 'unset-css';
    }
    return colKey === 'action' ? 'last-fix-col' : '';
  };

  const addRow = useCallback(
    (row) => {
      setDetailState((prev) => ({
        ...prev,
        orderDetails: [...prev.orderDetails, row],
      }));
    },
    [setDetailState],
  );

  const deleteRow = useCallback(
    (rowIdx) => {
      setDetailState((prev) => ({
        ...prev,
        orderDetails: prev.orderDetails.filter((_, idx) => idx !== rowIdx),
      }));
    },
    [setDetailState],
  );

  const handleCellChange = useCallback(
    (rowIdx, colKey, value) => {
      setDetailState((prev) => ({
        ...prev,
        orderDetails: prev.orderDetails.map((row, idx) => (idx === rowIdx ? { ...row, [colKey]: value } : row)),
      }));

      if (colKey === 'packType') {
        if (value) {
          clearValidationError('packType', rowIdx);
        } else {
          setValidationError('packType', t('PO_TEMPLATE.VALIDATION.PACK_TYPE_REQUIRED'), rowIdx);
        }
      }
    },
    [setDetailState, clearValidationError, setValidationError, t],
  );

  // Filter out action column in preview mode
  const visibleColumns = useMemo(() => {
    return isPreviewMode ? columns.filter((col) => col.key !== 'action') : columns;
  }, [columns, isPreviewMode]);

  return (
    <div className="inner-container order-details-section p-10">
      <div className="section-template-title">{t('PO_TEMPLATE.DETAIL_PAGE.ORDER_DETAILS')}</div>
      <div className="rp-scroll-container">
        <table id="PO_OrderDetails" className="table common-custom-table fixed-header mb-0">
          <thead className="thead-sticky">
            <tr>
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={`${col.key === 'skuNumber' ? 'w-120px' : 'w-224px'} ${col.key === 'packType' ? 'first-fix-col' : actionCol(col.key)} `}
                >
                  <div className="d-flex align-items-center">
                    {col.label}{' '}
                    {col.key === 'packType' && (
                      <div className="text-danger" style={{ marginLeft: 4 }}>
                        *
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tbody-sticky">
            {orderDetails.map((row, rowIdx) => (
              <React.Fragment key={rowIdx}>
                <tr>
                  {visibleColumns.map((col) => {
                    let cell = null;
                    if (col.type === 'select') {
                      const value = col?.options?.find((opt) => opt.value === row[col.key]) || null;
                      const hasError = col.key === 'packType' && detailState.validationErrors?.packType?.[rowIdx];
                      const showMultiSizeAddButton = col.key === 'packType' && row[col.key] === 'multi-size-packed' && !isPreviewMode;

                      cell = isPreviewMode ? (
                        <div className="form-control-plaintext text-bordered">{value?.label || value?.value || value || '--'}</div>
                      ) : (
                        <div className="w-100 m-w-150 common-react-select mb-0">
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                              <Select
                                name={col.key}
                                options={col?.options || []}
                                className="common-select"
                                classNamePrefix="common-select"
                                placeholder={`${t('COMMON_LABEL.SELECT')} ${col.label}`}
                                value={value}
                                onChange={(selected) => handleCellChange(rowIdx, col.key, selected ? selected.value : null)}
                                menuPosition="fixed"
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 150 }),
                                  menu: (base) => ({ ...base, zIndex: 150 }),
                                }}
                                maxMenuHeight={150}
                                isClearable
                                isDisabled={col.key !== 'packType'}
                              />
                              {hasError && <div className="invalid-feedback d-block">{detailState.validationErrors.packType[rowIdx]}</div>}
                            </div>
                            {showMultiSizeAddButton && (
                              <CustomTooltip tooltipText={t('PO_TEMPLATE.DETAIL_PAGE.ADD_SIZE')} placement={TOOLTIP_PLACEMENT}>
                                <button type="button" className="btn btn-outline ml-2" aria-label={t('PO_TEMPLATE.DETAIL_PAGE.ADD_SIZE')} disabled>
                                  <i className="icon-add" />
                                </button>
                              </CustomTooltip>
                            )}
                          </div>
                        </div>
                      );
                    } else if (col.type === 'date') {
                      cell = isPreviewMode ? (
                        <div className="form-control-plaintext text-bordered" style={{ width: '100%', padding: '8px' }}>
                          {row[col.key] ? new Date(row[col.key]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '--'}
                        </div>
                      ) : (
                        <CustomDatePicker
                          keyProp={col.key + rowIdx}
                          dateFormat="dd-MMM-yyyy"
                          valueProp={row[col.key] || null}
                          name={col.key}
                          disable
                          placeholder={`${t('COMMON_LABEL.SELECT')} ${col.label}`}
                          onChange={(date) => handleCellChange(rowIdx, col.key, date)}
                        />
                      );
                    } else if (col.type === 'action') {
                      cell = !isPreviewMode ? (
                        <div className="action-icon-container">
                          {(orderDetails?.length || 0) - 1 === rowIdx && (
                            <CustomTooltip tooltipText={t('COMMON_LABEL.ADD')} placement={TOOLTIP_PLACEMENT}>
                              <button type="button" className="btn ml-auto" aria-label={t('COMMON_LABEL.ADD')} onClick={() => addRow(row)}>
                                <i className="icon-Add f-20" />
                              </button>
                            </CustomTooltip>
                          )}
                          {orderDetails.length > 1 && (
                            <CustomTooltip tooltipText={t('COMMON_LABEL.DELETE')} placement={TOOLTIP_PLACEMENT}>
                              <button aria-label={t('COMMON_LABEL.DELETE')} type="button" className="btn p-0" onClick={() => deleteRow(rowIdx)}>
                                <i className="icon-Delete f-20" />
                              </button>
                            </CustomTooltip>
                          )}
                        </div>
                      ) : null;
                    } else {
                      cell = isPreviewMode ? (
                        <div className="form-control-plaintext text-bordered" style={{ width: '100%', padding: '8px' }}>
                          {row[col.key] ?? '--'}
                        </div>
                      ) : (
                        <input
                          type={col.type}
                          name={col.key}
                          value={row[col.key] ?? ''}
                          onChange={(e) =>
                            handleCellChange(rowIdx, col.key, (col.type === 'number' && (e.target.value === '' ? 0 : Number(e.target.value))) || e.target.value)
                          }
                          className="form-control text-bordered"
                          placeholder={col.type === 'number' ? `${t('COMMON_LABEL.ENTER')} ${col.label}` : ''}
                          style={{ width: '100%' }}
                          min={col?.min || undefined}
                          max={col?.max || undefined}
                          disabled
                        />
                      );
                    }
                    return (
                      <td key={col.key} className={`${col.key === 'packType' ? 'first-fix-col' : actionCol(col.key)}`}>
                        {cell}
                      </td>
                    );
                  })}
                </tr>
                {row?.packType === 'multi-destination-packed' && (
                  <tr>
                    <td colSpan={visibleColumns.length} style={{ zIndex: 4 }} className="p-10">
                      <div>
                        {t('PO_TEMPLATE.DETAIL_PAGE.MULTI_DESTINATION_ADDRESS')} : {t('PO_TEMPLATE.DETAIL_PAGE.AUTO_FILLED')}
                      </div>
                      <div>
                        {t('PO_TEMPLATE.DETAIL_PAGE.LOCATION_NAME')} : {t('PO_TEMPLATE.DETAIL_PAGE.AUTO_FILLED')}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

OrderDetails.propTypes = {
  isPreviewMode: PropTypes.bool,
};

OrderDetails.defaultProps = {
  isPreviewMode: false,
};
